import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import {Headline} from 'src/admin-rights-service/components/headline';
import {Skeleton} from 'src/components/skeleton';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';


import {CreateProfileHeadlineSkeleton} from './create-profile-headline.skeleton';
import {ProfileName} from './profile-name';

import type {AccessRightsProfile} from 'src/openapi/admin/models/AccessRightsProfile';

type Props = {
    isSaveBusy: boolean,
    isLoading: boolean,
    isProfileChanged: boolean,

    profile: AccessRightsProfile,

    onProfileNameSet: (profileName: string) => void,
    onSave: () => void,
};

export const CreateProfileHeadline = (props: Props) => {
    const {
        isSaveBusy,
        isLoading,
        isProfileChanged,

        profile,

        onProfileNameSet,
        onSave,
    } = props;

    const navigate = useNavigate();
    const {roleCreate} = useEventTracking();

    const createRole = useCallback(() => {
        onSave();
        roleCreate({
            isDuplicate: false,
            place: 'Role Profile',
        });
    }, [onSave, roleCreate]);

    const backToRoles = useCallback(() => {
        navigate('/roles/');
    }, [navigate]);

    return (
        <Skeleton shown={isLoading} view={<CreateProfileHeadlineSkeleton/>}>
            <Headline
                prefix={(
                    <Group>
                        <Button
                            variant="Type.SECONDARY"
                            startIcon={<Icon type={Icons.ANGLE_LEFT}/>}
                            onClick={backToRoles}>
                            Back to Roles
                        </Button>
                        <ProfileName
                            profileName={profile.profileName}
                            isPrimary={profile.isPrimary}
                            onSetName={onProfileNameSet}
                        />
                    </Group>
                )}
                header=""
                actionButtons={(
                    <Group>
                        {!profile.isPrimary && [
                            <Button key="Cancel" variant="Type.SECONDARY" onClick={backToRoles}>
                                Cancel
                            </Button>,
                            <Button
                                key="Create"
                                disabled={!isProfileChanged}
                                busy={isSaveBusy}
                                variant="Type.PRIMARY"
                                onClick={createRole}>
                                Create
                            </Button>,
                        ]}
                    </Group>
                )}
            />
        </Skeleton>
    );
};
